import { useCallback, useMemo } from "react";
import intl from "react-intl-universal";
import { Chart as ChartJS } from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import {
  TbActivity,
  TbAlertTriangle,
  TbBell,
  TbCircleCheck,
  TbClockBolt,
  TbKey,
  TbTrendingUp,
  TbWebhook,
  TbWorld,
} from "react-icons/tb";
import { SummaryCard } from "../../../../../Components/Atoms/SummaryCard";
import { useColorMode } from "../../../../../Hooks/useColorMode";
import { mapBaasApiOverviewMetrics } from "../../../../../Services/baasApiMetricsApi";
import {
  buildBaasHomeLineChartOptions,
  registerBaasHomeBarChart,
  registerBaasHomeLineChart,
} from "../../Processing/components/BaasHomeChartOptions";

registerBaasHomeLineChart(ChartJS);
registerBaasHomeBarChart(ChartJS);

const HEALTH_STYLES = {
  healthy: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  degraded: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  down: "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300",
};

const formatCount = (value) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(value);

const formatPercent = (value, fractionDigits = 1) =>
  `${new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)}%`;

const formatMs = (value) => `${formatCount(value)} ms`;

const buildStatusBarOptions = (isDark, formatTooltipValue) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: isDark ? "#333" : "#242424",
      titleFont: { size: 12, weight: "600" },
      bodyFont: { size: 12 },
      padding: 10,
      cornerRadius: 8,
      callbacks: {
        label: (context) => formatTooltipValue(context.parsed.y),
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        color: isDark ? "#6b7280" : "#9ca3af",
        font: { size: 11 },
      },
      border: { display: false },
    },
    y: {
      display: false,
      beginAtZero: true,
      grid: { display: false },
    },
  },
});

export const BaasApiOverviewPanel = ({
  filters,
  apiKeys = [],
  webhooks = [],
  metricsData = null,
  metricsLoading = false,
  pixMetrics = null,
}) => {
  const { isDark } = useColorMode();

  const metrics = useMemo(() => {
    const activeCount = apiKeys.filter((key) => key.status === "active").length;
    return mapBaasApiOverviewMetrics(metricsData, { activeApiKeys: activeCount });
  }, [metricsData, apiKeys]);

  const periodLabel = intl.get(`baas.api.period.${filters?.period || "24h"}`);

  const formatTooltipCount = useCallback((value) => formatCount(value), []);

  const lineChartOptions = useMemo(
    () => buildBaasHomeLineChartOptions(isDark, formatTooltipCount),
    [isDark, formatTooltipCount],
  );

  const barChartOptions = useMemo(
    () => buildStatusBarOptions(isDark, formatTooltipCount),
    [isDark, formatTooltipCount],
  );

  const requestsChartData = useMemo(
    () => ({
      labels: metrics.requestsOverTime.labels,
      datasets: [
        {
          label: intl.get("baas.api.overview.charts.requestsTitle"),
          data: metrics.requestsOverTime.values,
          borderColor: "#e7a73f",
          backgroundColor: "rgba(231, 167, 63, 0.12)",
          fill: true,
          pointBackgroundColor: "#e7a73f",
        },
      ],
    }),
    [metrics.requestsOverTime],
  );

  const statusChartData = useMemo(
    () => ({
      labels: [
        intl.get("baas.api.overview.charts.status2xx"),
        intl.get("baas.api.overview.charts.status4xx"),
        intl.get("baas.api.overview.charts.status5xx"),
      ],
      datasets: [
        {
          data: [
            metrics.statusBreakdown.success2xx,
            metrics.statusBreakdown.clientError4xx,
            metrics.statusBreakdown.serverError5xx,
          ],
          backgroundColor: ["#10b981", "#f59e0b", "#ef4444"],
          borderRadius: 6,
          maxBarThickness: 56,
        },
      ],
    }),
    [metrics.statusBreakdown],
  );

  const kpiCards = useMemo(
    () => [
      {
        key: "totalRequests",
        icon: TbWorld,
        title: intl.get("baas.api.overview.kpi.totalRequests"),
        value: formatCount(metrics.totalRequests),
        footerLabel: periodLabel,
      },
      {
        key: "successRate",
        icon: TbTrendingUp,
        title: intl.get("baas.api.overview.kpi.successRate"),
        value: formatPercent(metrics.successRate),
        footerLabel: intl.get("baas.api.overview.kpi.successRateSub"),
      },
      {
        key: "avgLatency",
        icon: TbClockBolt,
        title: intl.get("baas.api.overview.kpi.avgLatency"),
        value: formatMs(metrics.avgLatencyMs),
        footerLabel: intl.get("baas.api.overview.kpi.avgLatencySub"),
      },
      {
        key: "errorRate",
        icon: TbAlertTriangle,
        title: intl.get("baas.api.overview.kpi.errorRate"),
        value: formatPercent(metrics.errorRate),
        footerLabel: intl.get("baas.api.overview.kpi.errorRateSub"),
      },
      {
        key: "activeKeys",
        icon: TbKey,
        title: intl.get("baas.api.overview.kpi.activeKeys"),
        value: formatCount(metrics.activeApiKeys),
        footerLabel: intl.get("baas.api.overview.kpi.activeKeysSub"),
      },
      {
        key: "webhookDelivery",
        icon: TbWebhook,
        title: intl.get("baas.api.overview.kpi.webhookDelivery"),
        value: formatPercent(metrics.webhookDeliveryRate),
        footerLabel: intl.get("baas.api.overview.kpi.webhookDeliverySub", {
          count: webhooks.filter((item) => item.active).length,
        }),
      },
    ],
    [metrics, webhooks, periodLabel],
  );

  const pixCards = useMemo(() => {
    if (!pixMetrics || !(Number(pixMetrics.txCount) > 0 || Number(pixMetrics.volume) > 0)) {
      return [];
    }
    const formatMoney = (value) =>
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: pixMetrics.currency || "BRL",
        maximumFractionDigits: 0,
      }).format(Number(value) || 0);

    return [
      {
        key: "pixVolume",
        icon: TbWorld,
        title: intl.get("baas.api.overview.pix.volume"),
        value: formatMoney(pixMetrics.volume),
        footerLabel: periodLabel,
      },
      {
        key: "pixTx",
        icon: TbActivity,
        title: intl.get("baas.api.overview.pix.txCount"),
        value: formatCount(pixMetrics.txCount),
        footerLabel: intl.get("baas.api.overview.pix.txCountSub", {
          success: formatPercent(pixMetrics.successRate),
        }),
      },
      {
        key: "pixFailed",
        icon: TbAlertTriangle,
        title: intl.get("baas.api.overview.pix.failed"),
        value: formatCount(pixMetrics.failedCount),
        footerLabel: intl.get("baas.api.overview.pix.failedSub"),
      },
    ];
  }, [pixMetrics, periodLabel]);

  const healthStatus = metrics.healthSummary.status;

  if (metricsLoading) {
    return (
      <div className="rounded-lg border border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
        {intl.get("baas.api.overview.loading")}
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-5">
      <div>
        <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {intl.get("baas.api.overview.sectionTitle")}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.api.overview.sectionSubtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {kpiCards.map((card) => (
          <SummaryCard
            key={card.key}
            icon={card.icon}
            title={card.title}
            value={card.value}
            footerLabel={card.footerLabel}
          />
        ))}
      </div>

      {pixCards.length ? (
        <div>
          <p className="mb-3 text-sm font-semibold text-brand-dark dark:text-gray-100">
            {intl.get("baas.api.overview.pix.sectionTitle")}
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {pixCards.map((card) => (
              <SummaryCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={card.value}
                footerLabel={card.footerLabel}
              />
            ))}
          </div>
        </div>
      ) : null}

      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="min-w-0 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
            <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
              {intl.get("baas.api.overview.charts.requestsTitle")}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.api.overview.charts.requestsSubtitle")}
            </p>
          </div>
          <div className="h-56 px-3 py-4 sm:px-4">
            <Line data={requestsChartData} options={lineChartOptions} />
          </div>
        </div>

        <div className="min-w-0 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
            <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
              {intl.get("baas.api.overview.charts.statusTitle")}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.api.overview.charts.statusSubtitle")}
            </p>
          </div>
          <div className="h-56 px-3 py-4 sm:px-4">
            <Bar data={statusChartData} options={barChartOptions} />
          </div>
        </div>
      </div>

      <div className="min-w-0 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {intl.get("baas.api.overview.endpoints.title")}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.api.overview.endpoints.subtitle")}
          </p>
        </div>

        <div className="hidden border-b border-gray-100 bg-gray-50/80 px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40 lg:grid lg:grid-cols-[minmax(0,0.5fr)_minmax(0,1.2fr)_minmax(0,0.7fr)_minmax(0,0.7fr)_minmax(0,0.5fr)]">
          <span>{intl.get("baas.api.overview.endpoints.method")}</span>
          <span>{intl.get("baas.api.overview.endpoints.path")}</span>
          <span>{intl.get("baas.api.overview.endpoints.requests")}</span>
          <span>{intl.get("baas.api.overview.endpoints.avgLatency")}</span>
          <span>{intl.get("baas.api.overview.endpoints.errorRate")}</span>
        </div>

        {metrics.topEndpoints.length ? (
          metrics.topEndpoints.map((endpoint) => (
            <div
              key={`${endpoint.method}-${endpoint.path}`}
              className="grid grid-cols-1 gap-2 border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-brand-dark-border lg:grid-cols-[minmax(0,0.5fr)_minmax(0,1.2fr)_minmax(0,0.7fr)_minmax(0,0.7fr)_minmax(0,0.5fr)] lg:items-center"
            >
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.overview.endpoints.method")}
                </p>
                <span className="inline-flex rounded-md bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-brand-dark-border dark:text-gray-200">
                  {endpoint.method}
                </span>
              </div>
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.overview.endpoints.path")}
                </p>
                <code className="text-sm text-gray-600 dark:text-gray-300">{endpoint.path}</code>
              </div>
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.overview.endpoints.requests")}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">{formatCount(endpoint.requests)}</p>
              </div>
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.overview.endpoints.avgLatency")}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">{formatMs(endpoint.avgLatencyMs)}</p>
              </div>
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.overview.endpoints.errorRate")}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">{formatPercent(endpoint.errorRate)}</p>
              </div>
            </div>
          ))
        ) : (
          <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.api.overview.endpoints.empty")}
          </p>
        )}
      </div>

      <div className="min-w-0 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {intl.get("baas.api.overview.health.title")}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.api.overview.health.subtitle")}
          </p>
        </div>

        <div className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-brand-cream/60 dark:border-brand-dark-border dark:bg-brand-dark-elevated">
              <TbActivity className="h-4 w-4 text-brand-dark dark:text-gray-100" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {intl.get("baas.api.overview.health.overallStatus")}
              </p>
              <span
                className={`mt-1 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                  HEALTH_STYLES[healthStatus] ?? HEALTH_STYLES.healthy
                }`}
              >
                <TbCircleCheck className="h-3.5 w-3.5" />
                {intl.get(`baas.api.health.${healthStatus}`)}
              </span>
            </div>
          </div>

          <div className="flex min-w-0 items-start gap-2 rounded-lg border border-dashed border-gray-200 px-3 py-2.5 dark:border-brand-dark-border">
            <TbBell className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-brand-dark dark:text-gray-100">
                {intl.get("baas.api.overview.health.incidents")}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {metrics.healthSummary.incidents.length
                  ? metrics.healthSummary.incidents[0].title
                  : intl.get("baas.api.overview.health.noIncidents")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
